import React, { useEffect, useMemo, useState } from 'react';
import colors from '../../../constants/colors.constants';
import Typo from '../../../components/atom/Typo';
import useAuthStore from '../../../store/useAuthStore';
import strings from '../../../constants/strings.constants';
import ReservationItem from './components/ReservationItem';
import { useQuery } from 'react-query';
import { fetchReservationHistories } from '../../../apis/reservations';
import { Fade } from 'react-awesome-reveal';
import Skeleton from 'react-loading-skeleton';
import { ROUTER_PATHS } from '../../../routes/routes.constatns';
import { ReactComponent as IconBack } from '../../../assets/arrow-back.svg';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ReservationDetailModal from './components/ReservationDetailModal';
import { getPartnerName } from '../../../utils/utils';
import AuthBottomSheet from '../../../components/compositions/AuthBottomSheet/AuthBottomSheet';

function ReservationHistoriesPage() {
  const [searchParams] = useSearchParams();
  const reservationId = searchParams.get('reservationId') || undefined;
  const isDetailModeVisible = !!reservationId;
  const { partner, customer } = useAuthStore();
  const [noData, setNoData] = useState(false);
  const [selectedReservationId, setSelectedReservationId] = useState<
    number | undefined
  >(reservationId ? Number(reservationId) : undefined);
  const [detailModalVisible, setDetailModalVisible] =
    useState(isDetailModeVisible);
  const partnerName = useMemo(() => getPartnerName(partner), [partner]);
  const partnerLogo = partner?.partnerLogoImageUrl || '';
  const [authModalVisible, setAuthModalVisible] = useState(false);

  const {
    refetch,
    data: reservationHistories = [],
    isLoading,
  } = useQuery({
    queryKey: `FETCH_RESERVATION_HISTORIES_${partner?.id}`,
    queryFn: async () => {
      return fetchReservationHistories(customer?.token ?? '');
    },
    retry: false,
    enabled: !!customer,
    refetchOnWindowFocus: false,
    onSuccess: () => {
      setNoData(false);
    },
    onError: () => {
      setNoData(true);
    },
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!customer) {
      setAuthModalVisible(true);
    }
  }, [customer]);

  return (
    <div
      className="h-full flex flex-col"
      style={{
        backgroundColor: colors.greyHover,
      }}
    >
      <div
        className="min-h-[48px] flex items-center px-[20px] gap-2 justify-center relative"
        style={{
          borderBottom: `1px solid ${colors.defaultBorder}`,
          backgroundColor: 'white',
        }}
      >
        <div
          style={{
            padding: '5px',
            cursor: 'pointer',
            position: 'absolute',
            left: 16,
          }}
        >
          <IconBack
            onClick={() => {
              navigate(
                {
                  pathname: ROUTER_PATHS.RESERVATION,
                  search: location.search,
                },
                {
                  replace: true,
                },
              );
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          {partnerLogo && (
            <img width={24} height={24} src={partnerLogo} alt="병원로고" />
          )}
          <Typo type="b-16">{partnerName}</Typo>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-[24px] px-[20px]">
        <Typo type="b-18">{strings.reservationHistories}</Typo>
        <Fade duration={200}>
          <div className="flex flex-col gap-3 mt-3">
            {noData && (
              <Typo type="r-16" color={colors.subTitle}>
                {strings.noReservationHistories}
              </Typo>
            )}
            {isLoading ? (
              <Skeleton
                style={{
                  height: 100,
                  marginBottom: 12,
                  lineHeight: 1.5,
                  borderRadius: 16,
                }}
                count={5}
              />
            ) : (
              reservationHistories.map((reservation) => (
                <ReservationItem
                  key={reservation.id}
                  reservation={reservation}
                  onClick={(reservation) => {
                    setSelectedReservationId(reservation.id);
                    setDetailModalVisible(true);
                  }}
                />
              ))
            )}
          </div>
        </Fade>
      </div>
      <ReservationDetailModal
        visible={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          refetch();
        }}
        reservationId={selectedReservationId}
      />
      <AuthBottomSheet
        visible={authModalVisible}
        onCancel={() => {
          setAuthModalVisible(false);
        }}
        onAuthComplete={() => {}}
      />
    </div>
  );
}

export default ReservationHistoriesPage;
